import * as Yup from 'yup';
import {
  educationExperienceConfigDefaults,
  getHcpSignupWizardAttachmentFileValidationOpts,
} from './hcpSignupDefaults';
import {
  normalizeWizardDateInput,
  todayIsoDateLocal,
  isSignupStartIsoDateAllowed,
  isSignupEndIsoDateAllowed,
} from './hcpSignupDateUtils';
import { validateIdFile } from './generalInfoSchema';

const ACC = ['accredited', 'not_accredited', 'pending'];
const YES_NO = ['yes', 'no'];

/** Start dates: on or before today. */
function withStartDateTest(schema) {
  return schema.test(
    'start-date',
    'Cannot be in the future',
    isSignupStartIsoDateAllowed
  );
}

/** End dates: today or later. */
function withEndDateTest(schema) {
  return schema.test(
    'end-date',
    'Must be today or in the future',
    isSignupEndIsoDateAllowed
  );
}

/**
 * @param {object} edu merged education env config
 */
function educationRowSchema(edu) {
  const c = { ...educationExperienceConfigDefaults.education, ...edu };
  const wizardAttachOpts = getHcpSignupWizardAttachmentFileValidationOpts();

  const countryField = c.countryOfCertificateRequired
    ? Yup.string().trim().required('Country of certificate is required')
    : Yup.string().trim().nullable();

  const authorityField = c.authorityNameRequired
    ? Yup.string()
        .trim()
        .required('Issuing / certificate authority name is required')
        .min(
          c.authorityNameMinLength,
          `Authority name must be ${c.authorityNameMinLength}–${c.authorityNameMaxLength} characters`
        )
        .max(c.authorityNameMaxLength)
    : Yup.string()
        .trim()
        .nullable()
        .test(
          'auth-len',
          `Authority name must be ${c.authorityNameMinLength}–${c.authorityNameMaxLength} characters when provided`,
          (v) => {
            if (!v || !String(v).trim()) return true;
            const s = String(v).trim();
            return (
              s.length >= c.authorityNameMinLength &&
              s.length <= c.authorityNameMaxLength
            );
          }
        );

  const fieldOfStudyField = c.fieldOfStudyRequired
    ? Yup.string()
        .trim()
        .required('Field of study is required')
        .min(
          c.fieldOfStudyMinLength,
          `Field of study must be ${c.fieldOfStudyMinLength}–${c.fieldOfStudyMaxLength} characters`
        )
        .max(c.fieldOfStudyMaxLength)
    : Yup.string()
        .trim()
        .nullable()
        .test(
          'fos-len',
          `Field of study must be ${c.fieldOfStudyMinLength}–${c.fieldOfStudyMaxLength} characters when provided`,
          (v) => {
            if (!v || !String(v).trim()) return true;
            const s = String(v).trim();
            return (
              s.length >= c.fieldOfStudyMinLength &&
              s.length <= c.fieldOfStudyMaxLength
            );
          }
        );

  const studyYearsField = c.studyYearsRequired
    ? Yup.number()
        .transform((val, orig) =>
          orig === '' || orig == null ? undefined : Number(orig)
        )
        .typeError('Enter a whole number')
        .integer('Enter a whole number')
        .min(
          c.studyYearsMin,
          `Between ${c.studyYearsMin} and ${c.studyYearsMax}`
        )
        .max(
          c.studyYearsMax,
          `Between ${c.studyYearsMin} and ${c.studyYearsMax}`
        )
        .required('Years of study is required')
    : Yup.number()
        .transform((val, orig) =>
          orig === '' || orig == null ? undefined : Number(orig)
        )
        .nullable()
        .typeError('Enter a whole number')
        .integer('Enter a whole number')
        .test(
          'sy-range',
          `Between ${c.studyYearsMin} and ${c.studyYearsMax} when provided`,
          (v) => {
            if (v == null || v === '') return true;
            return v >= c.studyYearsMin && v <= c.studyYearsMax;
          }
        );

  const accStatusField = c.accreditationStatusRequired
    ? Yup.string()
        .oneOf(ACC, 'Select a valid status')
        .required('Accreditation status is required')
    : Yup.string()
        .nullable()
        .test('acc', 'Select a valid status', (v) => !v || ACC.includes(v));

  return Yup.object({
    countryOfCertificate: countryField,
    degreeName: Yup.string()
      .trim()
      .required('Degree name is required')
      .min(
        c.degreeMinLength,
        `Degree name must be ${c.degreeMinLength}–${c.degreeMaxLength} characters`
      )
      .max(c.degreeMaxLength),
    institutionName: Yup.string()
      .trim()
      .required('Institution name is required')
      .min(
        c.institutionMinLength,
        `Institution name must be ${c.institutionMinLength}–${c.institutionMaxLength} characters`
      )
      .max(c.institutionMaxLength),
    authorityName: authorityField,
    fieldOfStudy: fieldOfStudyField,
    graduationDate: withStartDateTest(
      Yup.string()
        .transform((v) =>
          v == null ? '' : normalizeWizardDateInput(String(v))
        )
        .required('Graduation date is required')
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date')
    ),
    studyYears: studyYearsField,
    accreditationStatus: accStatusField,
    accreditingAuthority: Yup.string().when('accreditationStatus', {
      is: 'accredited',
      then: (sch) =>
        sch
          .trim()
          .required('Required when accredited')
          .min(
            c.authorityNameMinLength,
            `Accrediting authority must be ${c.authorityNameMinLength}–${c.authorityNameMaxLength} characters`
          )
          .max(c.authorityNameMaxLength),
      otherwise: (sch) => sch.notRequired(),
    }),
    accreditationFile: Yup.mixed().nullable(),
    existingAccreditationDocumentUploadId: Yup.number().nullable().optional(),
    _rowKey: Yup.string().nullable(),
  })
    .test(
      'accreditation-document',
      'Accreditation document is required when status is accredited',
      function checkAccDoc(row) {
        if (!row || row.accreditationStatus !== 'accredited') return true;
        const hasFile = row.accreditationFile instanceof File;
        const hasExisting =
          row.existingAccreditationDocumentUploadId != null &&
          Number.isFinite(Number(row.existingAccreditationDocumentUploadId));
        if (hasFile || hasExisting) return true;
        const filePath = this.path
          ? `${this.path}.accreditationFile`
          : 'accreditationFile';
        return this.createError({
          path: filePath,
          message: 'Upload an accreditation document or keep the uploaded file',
        });
      }
    )
    .test(
      'accreditation-file-valid',
      'Invalid accreditation document',
      function accFileValid(row) {
        if (!(row?.accreditationFile instanceof File)) return true;
        const err = validateIdFile(row.accreditationFile, wizardAttachOpts);
        if (err) {
          const filePath = this.path
            ? `${this.path}.accreditationFile`
            : 'accreditationFile';
          return this.createError({
            path: filePath,
            message: err,
          });
        }
        return true;
      }
    );
}

function professionalExperienceSchema(exp) {
  const c = { ...educationExperienceConfigDefaults.experience, ...exp };
  const wizardAttachOpts = getHcpSignupWizardAttachmentFileValidationOpts();

  const addlBlockActive = (ex) =>
    Boolean(
      c.additionalExperienceSectionEnabled &&
      ex?.internshipTraining === 'yes' &&
      String(ex?.additionalExperience || '').toLowerCase() === 'yes'
    );

  const trainingField = c.internshipTrainingRequired
    ? Yup.string()
        .oneOf(YES_NO, 'Select Yes or No')
        .required('Select Yes or No')
    : Yup.string()
        .nullable()
        .transform((v) => (v === '' ? undefined : v))
        .test('yn', 'Select Yes or No', (v) => v == null || YES_NO.includes(v));

  const base = Yup.object({
    internshipTraining: trainingField,
    postGraduationInternship: Yup.string().when('internshipTraining', {
      is: 'yes',
      then: () =>
        c.postGradInternRequired
          ? Yup.string()
              .oneOf(YES_NO, 'Select Yes or No')
              .required('Select Yes or No')
          : Yup.string()
              .nullable()
              .transform((v) => (v === '' ? undefined : v))
              .test(
                'pg-yn',
                'Select Yes or No',
                (v) => v == null || YES_NO.includes(v)
              ),
      otherwise: (sch) => sch.nullable().optional(),
    }),
    internshipCompletion: Yup.string().when('internshipTraining', {
      is: 'yes',
      then: () =>
        c.internshipCompletionRequired
          ? Yup.string()
              .oneOf(YES_NO, 'Select Yes or No')
              .required('Select Yes or No')
          : Yup.string()
              .nullable()
              .transform((v) => (v === '' ? undefined : v))
              .test(
                'ic-yn',
                'Select Yes or No',
                (v) => v == null || YES_NO.includes(v)
              ),
      otherwise: (sch) => sch.nullable().optional(),
    }),
    internshipStart: Yup.string().when('internshipTraining', {
      is: 'yes',
      then: (sch) =>
        withStartDateTest(
          sch
            .transform((v) =>
              v == null ? '' : normalizeWizardDateInput(String(v))
            )
            .required('Required when internship / training is Yes')
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date')
        ),
      otherwise: (sch) => sch.optional().nullable(),
    }),
    internshipEnd: Yup.string().when('internshipTraining', {
      is: 'yes',
      then: (sch) =>
        withEndDateTest(
          sch
            .transform((v) =>
              v == null ? '' : normalizeWizardDateInput(String(v))
            )
            .required('Required when internship / training is Yes')
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date')
            .test('order', 'Must be on or after start date', function order(v) {
              const start = this.parent.internshipStart;
              if (!v || !start) return true;
              const a = normalizeWizardDateInput(String(start));
              const b = normalizeWizardDateInput(String(v));
              if (
                !/^\d{4}-\d{2}-\d{2}$/.test(a) ||
                !/^\d{4}-\d{2}-\d{2}$/.test(b)
              )
                return true;
              return b >= a;
            })
        ),
      otherwise: (sch) => sch.optional().nullable(),
    }),
    internshipCertificateFile: Yup.mixed().nullable(),
    existingInternshipCertificateUploadId: Yup.number().nullable().optional(),
    additionalExperience: Yup.string().when('internshipTraining', {
      is: 'yes',
      then: () => {
        if (!c.additionalExperienceSectionEnabled) {
          return Yup.string().nullable().optional();
        }
        if (c.additionalExperienceRequiredWhenInternshipYes) {
          return Yup.string()
            .oneOf(YES_NO, 'Select Yes or No')
            .required('Select Yes or No');
        }
        return Yup.string()
          .nullable()
          .transform((v) => (v === '' ? undefined : v))
          .test(
            'ae-yn',
            'Select Yes or No',
            (v) => v == null || YES_NO.includes(v)
          );
      },
      otherwise: (sch) => sch.nullable().optional(),
    }),
    additionalExperienceStart: Yup.string().when(
      ['internshipTraining', 'additionalExperience'],
      {
        is: (it, ae) =>
          it === 'yes' &&
          c.additionalExperienceSectionEnabled &&
          String(ae || '').toLowerCase() === 'yes',
        then: (sch) =>
          withStartDateTest(
            sch
              .transform((v) =>
                v == null ? '' : normalizeWizardDateInput(String(v))
              )
              .required('Required when additional experience is Yes')
              .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date')
          ),
        otherwise: (sch) => sch.optional().nullable(),
      }
    ),
    additionalExperienceEnd: Yup.string().when(
      ['internshipTraining', 'additionalExperience'],
      {
        is: (it, ae) =>
          it === 'yes' &&
          c.additionalExperienceSectionEnabled &&
          String(ae || '').toLowerCase() === 'yes',
        then: (sch) =>
          withEndDateTest(
            sch
              .transform((v) =>
                v == null ? '' : normalizeWizardDateInput(String(v))
              )
              .required('Required when additional experience is Yes')
              .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date')
              .test(
                'addl-order',
                'Must be on or after start date',
                function order(v) {
                  const start = this.parent.additionalExperienceStart;
                  if (!v || !start) return true;
                  const a = normalizeWizardDateInput(String(start));
                  const b = normalizeWizardDateInput(String(v));
                  if (
                    !/^\d{4}-\d{2}-\d{2}$/.test(a) ||
                    !/^\d{4}-\d{2}-\d{2}$/.test(b)
                  )
                    return true;
                  return b >= a;
                }
              )
          ),
        otherwise: (sch) => sch.optional().nullable(),
      }
    ),
    additionalExperienceCertificateFile: Yup.mixed().nullable(),
    existingAdditionalExperienceCertificateUploadId: Yup.number()
      .nullable()
      .optional(),
    undergraduateInternship: Yup.string().optional().nullable().max(8000),
    undergraduateInternshipStart: withStartDateTest(
      Yup.string()
        .transform((v) =>
          v == null || v === '' ? '' : normalizeWizardDateInput(String(v))
        )
        .nullable()
    ),
    undergraduateInternshipEnd: withEndDateTest(
      Yup.string()
        .transform((v) =>
          v == null || v === '' ? '' : normalizeWizardDateInput(String(v))
        )
        .nullable()
    ),
    undergraduateInternshipCertificateFile: Yup.mixed().nullable(),
    existingUndergraduateInternshipCertificateUploadId: Yup.number()
      .nullable()
      .optional(),
    relevantAttachmentFiles: Yup.array()
      .of(Yup.mixed())
      .max(c.relevantAttachmentsMax),
    existingRelevantAttachmentIds: Yup.array()
      .of(Yup.number())
      .nullable()
      .optional(),
  })
    .test(
      'internship-certificate-when-completed',
      'Internship completion certificate',
      function checkCert(ex) {
        if (!c.internshipCertificateRequiredWhenInternshipCompletionYes)
          return true;
        if (!ex || ex.internshipTraining !== 'yes') return true;
        if (String(ex.internshipCompletion || '').toLowerCase() !== 'yes')
          return true;
        const hasFile = ex.internshipCertificateFile instanceof File;
        const prev =
          ex.existingInternshipCertificateUploadId != null &&
          Number.isFinite(Number(ex.existingInternshipCertificateUploadId));
        if (hasFile || prev) return true;
        const filePath = this.path
          ? `${this.path}.internshipCertificateFile`
          : 'internshipCertificateFile';
        return this.createError({
          path: filePath,
          message: 'Certificate is required when internship is completed',
        });
      }
    )
    .test(
      'additional-experience-certificate',
      'Additional experience certificate',
      function addlCert(ex) {
        if (!addlBlockActive(ex)) return true;
        const hasFile = ex.additionalExperienceCertificateFile instanceof File;
        const prev =
          ex.existingAdditionalExperienceCertificateUploadId != null &&
          Number.isFinite(
            Number(ex.existingAdditionalExperienceCertificateUploadId)
          );
        if (hasFile || prev) return true;
        const filePath = this.path
          ? `${this.path}.additionalExperienceCertificateFile`
          : 'additionalExperienceCertificateFile';
        return this.createError({
          path: filePath,
          message: 'Certificate is required when additional experience is Yes',
        });
      }
    )
    .test(
      'undergraduate-dates-pair',
      'Undergraduate internship dates',
      function ugDates(ex) {
        const ugS = ex?.undergraduateInternshipStart;
        const ugE = ex?.undergraduateInternshipEnd;
        const hasS = ugS != null && String(ugS).trim() !== '';
        const hasE = ugE != null && String(ugE).trim() !== '';
        if (!hasS && !hasE) return true;
        const pfx = this.path ? `${this.path}.` : '';
        if (!hasS) {
          return this.createError({
            path: `${pfx}undergraduateInternshipStart`,
            message: 'Enter both start and end dates',
          });
        }
        if (!hasE) {
          return this.createError({
            path: `${pfx}undergraduateInternshipEnd`,
            message: 'Enter both start and end dates',
          });
        }
        const a = normalizeWizardDateInput(String(ugS));
        const b = normalizeWizardDateInput(String(ugE));
        if (!/^\d{4}-\d{2}-\d{2}$/.test(a)) {
          return this.createError({
            path: `${pfx}undergraduateInternshipStart`,
            message: 'Invalid date',
          });
        }
        if (a > todayIsoDateLocal()) {
          return this.createError({
            path: `${pfx}undergraduateInternshipStart`,
            message: 'Cannot be in the future',
          });
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(b)) {
          return this.createError({
            path: `${pfx}undergraduateInternshipEnd`,
            message: 'Invalid date',
          });
        }
        if (b < todayIsoDateLocal()) {
          return this.createError({
            path: `${pfx}undergraduateInternshipEnd`,
            message: 'Must be today or in the future',
          });
        }
        if (b < a) {
          return this.createError({
            path: `${pfx}undergraduateInternshipEnd`,
            message: 'Must be on or after start date',
          });
        }
        return true;
      }
    )
    .test(
      'undergraduate-certificate-required',
      'Undergraduate internship certificate',
      function ugCertReq(ex) {
        if (!c.undergraduateInternCertificateRequired) return true;
        const uText =
          ex?.undergraduateInternship != null
            ? String(ex.undergraduateInternship).trim()
            : '';
        const ugS = ex?.undergraduateInternshipStart;
        const ugE = ex?.undergraduateInternshipEnd;
        const hasUgS = ugS != null && String(ugS).trim() !== '';
        const hasUgE = ugE != null && String(ugE).trim() !== '';
        if (!uText && !hasUgS && !hasUgE) return true;
        const hasFile =
          ex.undergraduateInternshipCertificateFile instanceof File;
        const prev =
          ex.existingUndergraduateInternshipCertificateUploadId != null &&
          Number.isFinite(
            Number(ex.existingUndergraduateInternshipCertificateUploadId)
          );
        if (hasFile || prev) return true;
        const filePath = this.path
          ? `${this.path}.undergraduateInternshipCertificateFile`
          : 'undergraduateInternshipCertificateFile';
        return this.createError({
          path: filePath,
          message: 'Certificate is required',
        });
      }
    )
    .test(
      'internship-certificate-file-valid',
      'Invalid certificate file',
      function certFileValid(ex) {
        if (!(ex?.internshipCertificateFile instanceof File)) return true;
        const err = validateIdFile(
          ex.internshipCertificateFile,
          wizardAttachOpts
        );
        if (err) {
          const filePath = this.path
            ? `${this.path}.internshipCertificateFile`
            : 'internshipCertificateFile';
          return this.createError({
            path: filePath,
            message: err,
          });
        }
        return true;
      }
    )
    .test(
      'additional-experience-cert-file-valid',
      'Invalid additional experience certificate file',
      function addlCertValid(ex) {
        if (!(ex?.additionalExperienceCertificateFile instanceof File))
          return true;
        const err = validateIdFile(
          ex.additionalExperienceCertificateFile,
          wizardAttachOpts
        );
        if (err) {
          const filePath = this.path
            ? `${this.path}.additionalExperienceCertificateFile`
            : 'additionalExperienceCertificateFile';
          return this.createError({
            path: filePath,
            message: err,
          });
        }
        return true;
      }
    )
    .test(
      'undergraduate-cert-file-valid',
      'Invalid undergraduate certificate file',
      function ugCertValid(ex) {
        if (!(ex?.undergraduateInternshipCertificateFile instanceof File))
          return true;
        const err = validateIdFile(
          ex.undergraduateInternshipCertificateFile,
          wizardAttachOpts
        );
        if (err) {
          const filePath = this.path
            ? `${this.path}.undergraduateInternshipCertificateFile`
            : 'undergraduateInternshipCertificateFile';
          return this.createError({
            path: filePath,
            message: err,
          });
        }
        return true;
      }
    )
    .test(
      'relevant-attachments-files-valid',
      'Invalid attachment file',
      function relAttachValid(ex) {
        const files = ex?.relevantAttachmentFiles;
        if (!Array.isArray(files) || files.length === 0) return true;
        for (const f of files) {
          if (!(f instanceof File)) continue;
          const err = validateIdFile(f, wizardAttachOpts);
          if (err) {
            const filePath = this.path
              ? `${this.path}.relevantAttachmentFiles`
              : 'relevantAttachmentFiles';
            return this.createError({
              path: filePath,
              message: err,
            });
          }
        }
        return true;
      }
    );

  return base;
}

/** @param {object} [edu] overrides */
export function buildEducationItemSchema(edu) {
  return educationRowSchema(edu || {});
}

/** @param {object} [exp] overrides */
export function buildProfessionalExperienceSchema(exp) {
  return professionalExperienceSchema(exp || {});
}

/**
 * @param {object} [config] { education?: object, experience?: object }
 * @param {{ licenseTrackingRelaxed?: boolean }} [options]
 */
export function buildEducationExperienceSchema(config = {}, options = {}) {
  if (options.licenseTrackingRelaxed) {
    return Yup.object({
      educations: Yup.array(),
      experience: Yup.object().nullable(),
    });
  }
  const edu = {
    ...educationExperienceConfigDefaults.education,
    ...(config.education || {}),
  };
  const exp = {
    ...educationExperienceConfigDefaults.experience,
    ...(config.experience || {}),
  };
  return Yup.object({
    educations: Yup.array()
      .of(educationRowSchema(edu))
      .min(1, 'Provide at least one education entry'),
    experience: professionalExperienceSchema(exp),
  });
}
